import type { Field } from '../types';
import { apiClient } from './api/apiClient';
import { mapFieldDtoToField, type FieldApiDTO, type PageApiDTO } from '../types/api';

/**
 * 圃場サービス
 * バックエンドAPIから圃場データを取得する
 */
class FieldService {
  /**
   * 農場配下の圃場データを読み込む
   * @param farmId - 農場 ID
   * @returns 圃場一覧
   */
  async loadFieldsByFarm(farmId: number): Promise<Field[]> {
    const response = await apiClient.get<PageApiDTO<FieldApiDTO>>(`/farms/${farmId}/fields`, {
      size: '100',
    });
    return response.content.map(mapFieldDtoToField);
  }

  /**
   * バックエンドAPIから圃場を取得
   * @param fieldId - 圃場 ID
   * @returns 圃場データ、存在しない場合は null
   */
  async getFieldById(fieldId: string): Promise<Field | null> {
    try {
      const dto = await apiClient.get<FieldApiDTO>(`/fields/${fieldId}`);
      return mapFieldDtoToField(dto);
    } catch {
      return null;
    }
  }

  /**
   * 圃場情報を部分更新する（PATCH）。
   *
   * `area` は m² 単位で送信。`predictedYield` は `null` を明示的に送るとクリア、
   * プロパティ自体を省略すると変更なし。
   *
   * @param fieldId - 圃場 ID
   * @param payload - 更新内容（`area` / `predictedYield` のいずれか、または両方）
   * @returns 更新後の圃場データ
   */
  async updateField(
    fieldId: string,
    payload: { area?: number; predictedYield?: number | null }
  ): Promise<Field> {
    const dto = await apiClient.patch<FieldApiDTO>(`/fields/${fieldId}`, payload);
    return mapFieldDtoToField(dto);
  }
}

/** 圃場サービスのシングルトンインスタンス */
export const fieldService = new FieldService();
